import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { i18n } from '../i18n/i18n';
import { SupportedLanguageCode, supported_languages, normalizeLanguageCode } from '../i18n/translations';
import { storageService } from '../services/storageService';

interface LanguageContextType {
  current_language: SupportedLanguageCode;
  set_language: (lang: SupportedLanguageCode) => void;
  available_languages: Array<{ code: SupportedLanguageCode; label: string }>;
  use_system_language: boolean;
  set_use_system_language: (useSystem: boolean) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [current_language, setCurrentLanguage] = useState<SupportedLanguageCode>(i18n.language);
  const [use_system_language, setUseSystemLanguageState] = useState<boolean>(storageService.getUseSystemLanguage());

  useEffect(() => {
    // First-run: if no explicit language saved, default to using system language
    if (!storageService.hasLanguage()) {
      setUseSystemLanguageState(true);
      storageService.setUseSystemLanguage(true);
      const systemLang = normalizeLanguageCode(typeof navigator !== 'undefined' ? navigator.language : 'en');
      i18n.changeLanguage(systemLang);
    } else if (use_system_language) {
      const systemLang = normalizeLanguageCode(typeof navigator !== 'undefined' ? navigator.language : 'en');
      i18n.changeLanguage(systemLang);
    }

    const unsubscribe = i18n.subscribe(() => {
      setCurrentLanguage(i18n.language);
    });
    return unsubscribe;
  }, []);

  // React to changes to use_system_language at runtime
  useEffect(() => {
    if (use_system_language) {
      const systemLang = normalizeLanguageCode(typeof navigator !== 'undefined' ? navigator.language : 'en');
      i18n.changeLanguage(systemLang);
    }

    // Listen to system language changes where supported
    const onLangChange = () => {
      if (!use_system_language) return;
      const systemLang = normalizeLanguageCode(typeof navigator !== 'undefined' ? navigator.language : 'en');
      i18n.changeLanguage(systemLang);
    };
    window.addEventListener('languagechange', onLangChange as any);
    return () => window.removeEventListener('languagechange', onLangChange as any);
  }, [use_system_language]);

  // Tell the browser which language the page is in, so screen readers pronounce it correctly
  // and CSS hyphenation can break long words (e.g. "Beløn-ninger" in the bottom tab bar).
  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = current_language;
  }, [current_language]);

  const set_language = (lang: SupportedLanguageCode) => {
    i18n.changeLanguage(lang);
  };

  const set_use_system_language = (useSystem: boolean) => {
    setUseSystemLanguageState(useSystem);
    storageService.setUseSystemLanguage(useSystem);
    if (useSystem) {
      const systemLang = normalizeLanguageCode(typeof navigator !== 'undefined' ? navigator.language : 'en');
      i18n.changeLanguage(systemLang);
    }
  };

  const value: LanguageContextType = {
    current_language,
    set_language,
    available_languages: supported_languages,
    use_system_language,
    set_use_system_language,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider');
  return ctx;
};


